import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from "recharts";

import {
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Select,
  Form,
  Table,
  Progress,
  Space
} from "antd";
import ViewEmpCalendar from "./ViewEmpCalendar";
import { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import LeaveCalendar from "../employee/LeaveCalendar";

const { Title, Text } = Typography;
const { Option } = Select;

export default function ViewOneEmployeeRecord({ employee, isProfile }) {
  const currentMonth = dayjs().month() + 1;
  const currentYear = dayjs().year();
  const { authData, backUrl } = useContext(AuthContext);
  const [leaves_taken_breakdown, setLeavesTakenBreakdown] = useState({})

  console.log("Employee : ", employee)

  useEffect(() => {
    setLeavesTakenBreakdown(employee.leaves_taken_breakdown)
  })

  const [graphData, setGraphData] = useState({
    month: currentMonth,
    year: currentYear,
  });
  const [totalLeaves, setTotalLeaves] = useState([]);

  // ✅ Fetch total leaves with Bearer token
  const fetchTotalLeaves = async () => {
    try {
      const res = await axios.get(
        `${backUrl}/api/hr/leaves/?employee=${employee.id || employee.employee.id}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setTotalLeaves(res.data.results || []);
    } catch (err) {
      console.error("Error fetching total leaves:", err);
    }
  };

  useEffect(() => {
    if (employee?.id) fetchTotalLeaves();
  }, [employee]);

  // ✅ Filter leave data based on selected month and year
  const filteredLeaves = totalLeaves.filter((leave) => {
    const start = dayjs(leave.start_date);
    const matchesMonth =
      !graphData.month || start.month() + 1 === graphData.month;
    const matchesYear = !graphData.year || start.year() === graphData.year;
    return matchesMonth && matchesYear;
  });
  console.log("Employee", employee)

  // ✅ Compute leave counts by total days
  const leaveTypeCounts = filteredLeaves.reduce((acc, leave) => {
    const type = leave.leave_type || "Unknown";
    const days = Number(leave.leave_days_count) || 0;
    acc[type] = (acc[type] || 0) + days;
    return acc;
  }, {});

  const pieData = Object.entries(leaveTypeCounts).map(([name, value]) => ({
    name,
    value,
  }));

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28EFF"];

  const handleMonthChange = (date) => {
    setGraphData((prev) => ({
      ...prev,
      month: date ? date.month() + 1 : "",
    }));
  };

  const handleYearChange = (value) => {
    setGraphData((prev) => ({ ...prev, year: value }));
  };

  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const leaveColumns = [
    { title: "Leave Type", dataIndex: "leave_type", key: "leave_type" },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 300,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {text}
        </div>
      ),
    },
    { title: "Duration Type", dataIndex: "duration_type", key: "duration_type" },
    { title: "Leave Days", dataIndex: "leave_days_count", key: "leave_days_count" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          style={{
            color:
              status === "Approved"
                ? "green"
                : status === "Rejected"
                  ? "red"
                  : "orange",
            fontWeight: 500,
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Document",
      dataIndex: "leave_document",
      key: "leave_document",
      render: (file) =>
        file ? (
          <a href={file} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          "-"
        ),
    },
  ];

  // ✅ Summary (SL, CL, EL)
  const totalDaysByType = {
    SL: leaveTypeCounts["Sick Leave"] || 0,
    CL: leaveTypeCounts["Casual Leave"] || 0,
    EL: leaveTypeCounts["Earned Leave"] || 0,
  };

  return (
    <Card bordered={false} style={{ margin: "5px", width: "100%" }}>
      {/* ✅ Summary */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-around",
          alignItems: "center",
          gap: "10px",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        {/* <h5>SL Taken: {leaves_taken_breakdown?.sick_leave} / {employee.employee.actual_sick_leave === null? "0": employee.employee.actual_sick_leave}</h5>
        <h5>CL Taken: {leaves_taken_breakdown?.casual_leave} / {employee.employee.actual_casual_leave}</h5>
        <h5>EL Taken: {leaves_taken_breakdown?.earned_leave} / {employee.employee.actual_earned_leave === null? "0": employee.employee.actual_earned_leave}</h5> */}

        <Space direction="vertical" style={{ width: "100%", display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }} size="small">
          {/* SL */}
          <div>
            <Text strong>SL TAKEN: </Text>
            <Text>
              {employee.employee.leave_balance?.SL?.used ?? 0} /{" "}
              {employee.employee.actual_sick_leave ?? 0}
            </Text>
            <Progress
              percent={
                employee.employee.actual_sick_leave
                  ? (employee.employee.leave_balance?.SL?.used /
                    employee.employee.actual_sick_leave) *
                  100
                  : 0
              }
              strokeColor="red"
              showInfo={false}
              size={[200, 6]} // width: 200px, height: 6px (smaller)
              style={{ marginTop: 4 }}
            />
          </div>

          {/* CL */}
          <div>
            <Text strong>CL TAKEN: </Text>
            <Text>
              {employee.employee.leave_balance?.CL?.used ?? 0} /{" "}
              {employee.employee.actual_casual_leave ?? 0}
            </Text>
            <Progress
              percent={
                employee.employee.actual_casual_leave
                  ? (employee.employee.leave_balance?.CL?.used /
                    employee.employee.actual_casual_leave) *
                  100
                  : 0
              }
              strokeColor="red"
              showInfo={false}
              size={[200, 6]}
              style={{ marginTop: 4 }}
            />
          </div>
        </Space>
        {
          leaves_taken_breakdown?.lop_leave > 0 && (
            <h5 style={{ color: "red" }}>LOP Taken: {leaves_taken_breakdown?.lop_leave}</h5>

          )
        }

      </div>

      {/* ✅ Month / Year Filters */}
      <Form
        layout="inline"
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        <Form.Item label="Month">
          <DatePicker
            picker="month"
            onChange={handleMonthChange}
            allowClear
            value={graphData.month ? dayjs().month(graphData.month - 1) : null}
            style={{ width: 160 }}
          />
        </Form.Item>

        <Form.Item label="Year">
          <Select
            style={{ width: 140 }}
            placeholder="Select Year"
            onChange={handleYearChange}
            allowClear
            value={graphData.year}
          >
            {years.map((yr) => (
              <Option key={yr} value={yr}>
                {yr}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      {/* ✅ Leave Table */}
      <Table
        columns={leaveColumns}
        dataSource={filteredLeaves.map((item) => ({
          ...item,
          key: item.id,
        }))}
        pagination={{ pageSize: 5, responsive: true }}
        scroll={{ x: "max-content" }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: "#0e497b",
                  color: "white",
                  textAlign: "center",
                }}
              >
                {props.children}
              </th>
            ),
          },
        }}
      />

      {/* ✅ Month-Year Info */}
      <Row
        gutter={[16, 16]}
        justify="space-between"
        align="middle"
        style={{ marginTop: 20 }}
      >
        <Col xs={24} md={12}>
          <Text type="secondary">
            Showing leaves for: {graphData.month || "--"}/{graphData.year || "--"}
          </Text>
        </Col>
      </Row>

      {
        !isProfile && (
          <>

          </>
          // < Row gutter={[24, 24]} justify="center" style={{ marginTop: 30 }}>
          //   {/* Calendar Section */}
          //   <Col xs={24} md={14} lg={16}>
          //     <div
          //       style={{
          //         width: "100%",
          //         height: 450,
          //         overflowY: "auto",
          //         border: "1px solid #f0f0f0",
          //         borderRadius: "8px",
          //         padding: "10px",
          //       }}
          //     >
          //       <LeaveCalendar employee={employee} />
          //     </div>
          //   </Col>

          //   {/* Chart Section */}
          //   <Col xs={24} md={10} lg={8}>
          //     <div style={{ height: 300 }}>
          //       <ResponsiveContainer width="100%" height="100%">
          //         <BarChart
          //           data={pieData}
          //           margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
          //         >
          //           <CartesianGrid strokeDasharray="3 3" />
          //           <XAxis dataKey="name" />
          //           <YAxis />
          //           <Tooltip />
          //           <Legend />
          //           <Bar dataKey="value" fill="#0e497b">
          //             {pieData.map((entry, index) => (
          //               <Cell
          //                 key={`cell-${index}`}
          //                 fill={COLORS[index % COLORS.length]}
          //               />
          //             ))}
          //           </Bar>
          //         </BarChart>
          //       </ResponsiveContainer>
          //     </div>
          //   </Col>
          // </Row>
        )
      }

    </Card >
  );
}
